// =====================================================
// components/AudioFeedModal.tsx
// =====================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs, structure de données restent codés en dur, inchangés.

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, View, Text, Modal, TouchableOpacity,
  FlatList, ScrollView, StatusBar, ActivityIndicator,
  Platform, Dimensions,
} from 'react-native';
import { Ionicons }       from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Audio }          from 'expo-audio';
import * as Haptics       from 'expo-haptics';
import { useTranslation } from 'react-i18next';
import { SafeImage }      from './MediaViewers';
import { AudioFeedItem, GameType, NATIVE } from '../lib/feedHelpers';

const { width, height } = Dimensions.get('window');
const AUD_CLOSE_TOP = Platform.OS === 'ios' ? 54 : 30;

interface AudioFeedModalProps {
  visible:    boolean;
  items:      AudioFeedItem[];
  startIndex: number;
  onClose:    () => void;
  onVote:     (gameType: GameType, runId: string, voteForUserId: string, sessionId: string, currentlyVoted: boolean) => Promise<void>;
}

export default function AudioFeedModal({ visible, items, startIndex, onClose, onVote }: AudioFeedModalProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const flatRef      = useRef<FlatList>(null);
  const webScrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (!visible || items.length === 0) return;
    setCurrentIndex(startIndex);
    if (Platform.OS === 'web') {
      setTimeout(() => { webScrollRef.current?.scrollTo({ y: startIndex * height, animated: false }); }, 50);
    } else {
      flatRef.current?.scrollToIndex({ index: startIndex, animated: false });
    }
  }, [visible, startIndex]);

  if (!visible || items.length === 0) return null;

  return (
    <Modal visible={visible} animationType="slide" statusBarTranslucent onRequestClose={onClose}>
      <StatusBar hidden />
      <View style={styles.root}>
        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
          <Ionicons name="close" size={28} color="#FFF" />
        </TouchableOpacity>
        {Platform.OS === 'web' ? (
          <ScrollView
            ref={webScrollRef} style={{ flex: 1 }}
            pagingEnabled showsVerticalScrollIndicator={false} scrollEventThrottle={1}
            onScroll={e => {
              const idx = Math.round(e.nativeEvent.contentOffset.y / height);
              if (idx !== currentIndex) setCurrentIndex(idx);
            }}
          >
            {items.map((item, index) => (
              <View key={item.mediaId} style={{ width, height }}>
                <AudioFeedItemView item={item} isActive={index === currentIndex} onVote={onVote} />
              </View>
            ))}
          </ScrollView>
        ) : (
          <FlatList
            ref={flatRef} data={items} keyExtractor={item => item.mediaId}
            pagingEnabled showsVerticalScrollIndicator={false}
            snapToInterval={height} decelerationRate="fast"
            getItemLayout={(_, index) => ({ length: height, offset: height * index, index })}
            initialScrollIndex={startIndex}
            onMomentumScrollEnd={e => {
              const idx = Math.round(e.nativeEvent.contentOffset.y / height);
              setCurrentIndex(idx);
            }}
            renderItem={({ item, index }) => (
              <AudioFeedItemView item={item} isActive={index === currentIndex} onVote={onVote} />
            )}
          />
        )}
      </View>
    </Modal>
  );
}

function AudioFeedItemView({ item, isActive, onVote }: {
  item: AudioFeedItem; isActive: boolean;
  onVote: AudioFeedModalProps['onVote'];
}) {
  const { t } = useTranslation();
  const [sound,      setSound]      = useState<any>(null);
  const [isPlaying,  setIsPlaying]  = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [isLoading,  setIsLoading]  = useState(false);
  const [totalVotes, setTotalVotes] = useState(item.total_votes);
  const [userVoted,  setUserVoted]  = useState(item.user_voted);
  const [isVoting,   setIsVoting]   = useState(false);

  useEffect(() => { setTotalVotes(item.total_votes); setUserVoted(item.user_voted); }, [item]);

  useEffect(() => {
    let snd: any = null;
    if (!isActive) {
      setSound((prev: any) => {
        if (prev) { try { prev.unloadAsync(); } catch {} }
        return null;
      });
      setIsPlaying(false); setPositionMs(0);
      return;
    }
    async function loadAndPlay() {
      setIsLoading(true);
      try {
        if (Audio?.setAudioModeAsync) {
          await Audio.setAudioModeAsync({ playsInSilentModeIOS: true } as any);
        }
        if (Audio?.Sound?.createAsync) {
          const { sound: s } = await Audio.Sound.createAsync(
            { uri: item.audioUrl },
            { shouldPlay: true },
            (status: any) => {
              if (status.isLoaded) {
                setIsPlaying(status.isPlaying ?? false);
                setPositionMs(status.positionMillis ?? 0);
                setDurationMs(status.durationMillis ?? 0);
              }
            }
          );
          snd = s; setSound(s);
        }
      } catch {} finally { setIsLoading(false); }
    }
    loadAndPlay();
    return () => { if (snd) { try { snd.unloadAsync(); } catch {} } };
  }, [isActive, item.audioUrl]);

  const togglePlay = async () => {
    if (!sound || isLoading) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    try {
      if (isPlaying) { await sound.pauseAsync(); }
      else {
        if (durationMs > 0 && positionMs >= durationMs) { await sound.replayAsync(); }
        else { await sound.playAsync(); }
      }
    } catch {}
  };

  const handleVoteLocal = async () => {
    if (isVoting) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setIsVoting(true);
    const n = !userVoted; setUserVoted(n); setTotalVotes(p => n ? p + 1 : p - 1);
    try { await onVote(item.game_type, item.run_id, item.user_id, item.session_id, userVoted); }
    catch { setUserVoted(!n); setTotalVotes(p => n ? p - 1 : p + 1); }
    finally { setIsVoting(false); }
  };

  const progress  = durationMs > 0 ? positionMs / durationMs : 0;
  const fmtMs     = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const authorPrenom = item.author?.prenom || '';
  const authorNom    = item.author?.nom    || '';

  return (
    <View style={styles.item}>
      <LinearGradient colors={['#1E0A3C', '#080810']} style={StyleSheet.absoluteFill} />
      <View style={styles.albumArt}>
        <View style={styles.albumCircle}>
          <Ionicons name="musical-notes" size={64} color="#A855F7" />
        </View>
      </View>
      <View style={styles.trackInfo}>
        <Text style={styles.trackTitle}>{item.run_title}</Text>
        <Text style={styles.trackSub}>{t('audioFeedModal.runNumber', { number: item.run_number })}</Text>
        {item.description ? <Text style={styles.trackDesc} numberOfLines={2}>{item.description}</Text> : null}
      </View>
      <View style={styles.progressContainer}>
        <View style={styles.progressBg}>
          <View style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` as any }]} />
        </View>
        <View style={styles.timeRow}>
          <Text style={styles.timeText}>{fmtMs(positionMs)}</Text>
          <Text style={styles.timeText}>{fmtMs(durationMs)}</Text>
        </View>
      </View>
      <TouchableOpacity style={styles.playBtn} onPress={togglePlay} disabled={isLoading}>
        {isLoading
          ? <ActivityIndicator size="large" color="#A855F7" />
          : <Ionicons name={isPlaying ? 'pause-circle' : 'play-circle'} size={72} color="#A855F7" />
        }
      </TouchableOpacity>
      <LinearGradient colors={['transparent', 'rgba(0,0,0,0.75)']} style={styles.gradient} />
      <View style={styles.infoRow}>
        <SafeImage
          uri={item.author?.avatar_url}
          style={styles.avatar}
          resizeMode="cover"
          fallback={
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Text style={styles.avatarText}>{authorPrenom.charAt(0)}{authorNom.charAt(0)}</Text>
            </View>
          }
        />
        <View style={styles.infoText}>
          <Text style={styles.authorName}>{authorPrenom} {authorNom}</Text>
          <Text style={styles.runLabel}>{t('audioFeedModal.runLabel', { number: item.run_number, title: item.run_title })}</Text>
        </View>
      </View>
      <TouchableOpacity
        style={[styles.voteBtn, userVoted && styles.voteBtnVoted]}
        onPress={handleVoteLocal}
        disabled={isVoting}
        activeOpacity={0.85}
      >
        <Ionicons name={userVoted ? 'heart' : 'heart-outline'} size={26} color={userVoted ? '#FF4D6A' : '#FFF'} />
        <Text style={styles.voteCount}>{totalVotes}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  root:              { flex: 1, backgroundColor: '#080810' },
  closeBtn:          { position: 'absolute', top: AUD_CLOSE_TOP, right: 16, zIndex: 100, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 20, width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  item:              { width, height, overflow: 'hidden' },
  albumArt:          { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center' },
  albumCircle:       { width: 180, height: 180, borderRadius: 90, backgroundColor: 'rgba(168,85,247,0.15)', borderWidth: 2, borderColor: 'rgba(168,85,247,0.3)', justifyContent: 'center', alignItems: 'center' },
  trackInfo:         { position: 'absolute', top: 80, left: 24, right: 24, alignItems: 'center' },
  trackTitle:        { color: '#FFF', fontWeight: '800', fontSize: 18, textAlign: 'center' },
  trackSub:          { color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 4 },
  trackDesc:         { color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 6, textAlign: 'center' },
  progressContainer: { position: 'absolute', bottom: 230, left: 32, right: 32 },
  progressBg:        { height: 4, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, overflow: 'hidden' },
  progressFill:      { height: 4, backgroundColor: '#A855F7', borderRadius: 2 },
  timeRow:           { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  timeText:          { color: 'rgba(255,255,255,0.6)', fontSize: 11 },
  playBtn:           { position: 'absolute', bottom: 160, left: 0, right: 0, alignItems: 'center' },
  gradient:          { position: 'absolute', bottom: 0, left: 0, right: 0, height: 220 },
  infoRow:           { position: 'absolute', bottom: 60, left: 16, right: 80, flexDirection: 'row', alignItems: 'flex-end', gap: 12 },
  avatar:            { width: 44, height: 44, borderRadius: 22, borderWidth: 2, borderColor: '#FFF' },
  avatarFallback:    { backgroundColor: '#6200EA', alignItems: 'center', justifyContent: 'center' },
  avatarText:        { color: '#FFF', fontWeight: '800', fontSize: 14 },
  infoText:          { flex: 1, gap: 3 },
  authorName:        { color: '#FFF', fontWeight: '800', fontSize: 15 },
  runLabel:          { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  voteBtn:           { position: 'absolute', right: 16, bottom: 80, alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: 30, paddingHorizontal: 10, paddingVertical: 12 },
  voteBtnVoted:      { backgroundColor: 'rgba(255,77,106,0.2)' },
  voteCount:         { color: '#FFF', fontSize: 13, fontWeight: '800' },
});
